import { useState, useEffect, useRef, useLayoutEffect } from 'react';
import orderBy from 'lodash/orderBy';
import first from 'lodash/first';
import Autocomplete from '~/components/final_form/select/autocomplete';
import type { AutocompleteProps, Value } from '~/components/final_form/select/types';
import { AutocompleteStyled } from './styled';
import useWidth from './use_width';

type NavigateDropdownProps = AutocompleteProps<Value>;

// a long label is cut in the field and wraps in the list, instead of widening the page
const MAX_WIDTH = 600;

function NavigateDropdown(props: NavigateDropdownProps) {
  const { value, options, onOpen, sx } = props;
  const defaultLabel = value?.label ? value?.label : options.find((option) => option.value === value)?.label;
  const [label, setLabel] = useState(defaultLabel);
  const containerRef = useRef<HTMLDivElement>(null);
  const hiddenSpanRef = useRef<HTMLSpanElement>(null);

  useEffect(() => setLabel(defaultLabel), [defaultLabel]);

  // Sync the actual input element's computed font to the hidden measurement span so
  // that text width is measured accurately even when wrappers override the font size
  // (e.g. AutocompleteOrganizationsStyled sets pxToRem(16) instead of pxToRem(24)).
  useLayoutEffect(() => {
    if (!containerRef.current || !hiddenSpanRef.current) return;
    const inputEl = containerRef.current.querySelector<HTMLInputElement>('.MuiOutlinedInput-input');
    if (!inputEl) return;
    const computed = window.getComputedStyle(inputEl);
    const span = hiddenSpanRef.current;
    span.style.fontSize = computed.fontSize;
    span.style.fontFamily = computed.fontFamily;
    span.style.fontWeight = computed.fontWeight;
    span.style.letterSpacing = computed.letterSpacing;
  });

  const labelWidth = Math.min(useWidth(label, hiddenSpanRef), MAX_WIDTH);

  return (
    <AutocompleteStyled ref={containerRef}>
      <span
        ref={hiddenSpanRef}
        style={{
          position: 'absolute',
          visibility: 'hidden',
          whiteSpace: 'nowrap',
          pointerEvents: 'none'
        }}
      >
        {label}
      </span>
      <Autocomplete
        {...props}
        sx={{ width: labelWidth, minWidth: 200, maxWidth: '100%', ...sx }}
        slotProps={{
          popper: {
            style: { width: labelWidth, maxWidth: '100%' }
          }
        }}
        disableClearable
        disablePortal
        onOpen={() => {
          const longestLabel = first(orderBy(options, (option) => option.label.length, ['desc']))?.label;
          setLabel(longestLabel);
          onOpen?.();
        }}
        onClose={() => setLabel(defaultLabel)}
      />
    </AutocompleteStyled>
  );
}

export default NavigateDropdown;
